import React, { useLayoutEffect, useRef, useState } from 'react'
import { TenantLogo, TenantName } from '@bizfirst/common-react'
import { LOGIN_APP_URL } from '@passport/app-urls'
import { enableConsoleLogs } from '@passport/api-client'

// Login/sign-in flow diagnostic logging below (auto-redirect attempts + loop detection) — gated
// behind enableConsoleLogs.loginProcess. DO NOT remove these console.log statements in future
// changes; toggle the flag instead. This component renders the exact "Sign-in keeps redirecting
// back here..." message users see when the loop trips, so its own attempt-counting logic is a
// primary diagnostic point. See debugFlags.ts in @passport/api-client.

/**
 * sessionStorage key tracking consecutive auto-redirects to the login app
 * without a handoff back here ever completing successfully. Incremented on
 * every LoginRedirectPage mount, reset to 0 by the consuming app's own
 * session-check effect the moment it actually finishes adopting/verifying a
 * session (see saas-tenant-administration/atlas-forms form-studio App.tsx).
 *
 * Exists to close a real incident: this app's own token-adoption code used
 * to only adopt a token handed back from `?token=...` when it didn't
 * already have ANY token persisted locally — so a stale/expired token left
 * over from an earlier session on this origin permanently blocked adopting
 * the fresh one, `verifyToken()` kept failing against the stale copy, and
 * the app kept bouncing back here, which kept sending the browser back to
 * an already-authenticated login app that kept reissuing (and having
 * discarded) a fresh token — an infinite redirect loop with no visible
 * error. That root cause is fixed in the consuming apps' own token
 * adoption now, but this counter stays as defense-in-depth: if a FRESH,
 * just-issued token still fails verification (a real backend/session
 * problem, not a stale-local-copy problem), auto-redirecting forever would
 * reproduce the same symptom for a different reason. sessionStorage (not a
 * ref) is required here specifically because each bounce is a full page
 * navigation — component state doesn't survive it.
 */
export const SSO_HANDOFF_ATTEMPT_STORAGE_KEY = 'passport.sso.handoffAttempts'
const MAX_AUTO_REDIRECT_ATTEMPTS = 3

/**
 * Which login methods a consuming app wants enabled on the central login
 * app's page. All optional and all default to the login app's own
 * `<AuthProvider config={{ features: {...} }}>` behavior (see
 * `src/passport/apps/login/src/App.tsx`) when omitted — only fields a
 * consuming app explicitly sets are sent, so an app that doesn't care about
 * any of this can ignore `methods` entirely and get today's exact behavior.
 *
 * Field names intentionally mirror `@passport/auth-core`'s own
 * `FeatureFlags` naming where a flag already exists there (`googleOAuth`),
 * shortened for this outward-facing prop since every field here is
 * necessarily login-page-scoped already (no need to repeat "Login" the way
 * `emailPasswordLogin` does internally).
 */
export interface LoginMethodFlags {
  /** Email/password form. Defaults to `true` (the login app's current
   *  always-on behavior) if omitted. Maps to `FeatureFlags.emailPasswordLogin`
   *  on the login app's side. */
  regular?: boolean
  /** Google OAuth "Sign in with Google" button. Defaults to whatever the
   *  login app's own `<AuthProvider>` config already has (`true`, per its
   *  current `App.tsx`) if omitted. Maps to `FeatureFlags.googleOAuth`. */
  google?: boolean
  /** Facebook OAuth — NOT YET IMPLEMENTED anywhere in this codebase (no
   *  Facebook login UI or backend integration exists as of this writing —
   *  verified by repo-wide search; only a `FACEBOOK` entry in
   *  `@passport/constants`' `OAUTH_PROVIDERS` list). Accepted here for a
   *  forward-compatible API shape; passing `true` today has no visible
   *  effect since the login app has no Facebook button to show. Maps to
   *  `FeatureFlags.facebookOAuth`. */
  facebook?: boolean
}

export interface LoginRedirectPageProps {
  /** App name shown under the logo (e.g. "App Template"). */
  appName: string
  /** Backend API base URL the tenant logo is fetched from — passed straight
   *  through to `TenantLogo`. Optional: defaults to `VITE_API_BASE_URL`/the
   *  current origin, same as `TenantLogo` itself; pass explicitly if this
   *  app already computes its own API base URL for other calls. */
  apiBaseUrl?: string
  /** Where the login app should send the browser back to after a real login.
   *  Defaults to `window.location.href` (this app's current full URL, path
   *  included) — matches `SsoAuthGate`'s own explicit `returnUrl={window.location.href}`.
   *  Previously defaulted to `window.location.origin` (bare origin, no path), which meant
   *  every app calling this component directly without an explicit `returnUrl` (e.g.
   *  form-studio, saas-tenant-administration) sent the login app a returnUrl with no app
   *  path at all — after login, the browser was sent back to the bare origin, which nginx
   *  resolves to whichever app is configured as the default landing page (flowstudio on the
   *  test VPS), not the app the user actually came from. Override only if a different
   *  landing route is wanted. */
  returnUrl?: string
  /** Which login methods this app wants enabled on the login app's page.
   *  Omit entirely (or omit individual fields) to use the login app's own
   *  defaults — see `LoginMethodFlags`. */
  methods?: LoginMethodFlags
  /**
   * Which cross-app handoff mechanism this app expects back from the login app.
   * Omit (or `'token'`) for the legacy `?token=<raw JWT>` handoff every app still
   * uses today — `ReturnUrlRedirectHandler.tsx` on the login-app side falls back to
   * this by default, so existing apps are unaffected by this prop's existence.
   * Pass `'code'` to opt into the purpose-built exchange-code handoff instead (a
   * single-use, ~30s-TTL opaque code redeemed server-side via
   * `AuthClient.redeemHandoffCode()` — see that method's doc comment) — the real
   * JWT never touches the URL/browser history/server logs. Only apps that have
   * migrated their own token-adoption code to redeem a code (not read `?token=`
   * directly) should pass `'code'`.
   */
  handoffMode?: 'token' | 'code'
}

/** Query param each `LoginMethodFlags` field is encoded as on the redirect
 *  URL — kept in one place so the login app's own param-reading side (see
 *  `src/passport/apps/login/src/useLoginMethodOverrides.ts`) can be pointed
 *  back here in comments without the names drifting apart silently. */
const METHOD_FLAG_PARAMS: Record<keyof LoginMethodFlags, string> = {
  regular: 'regular',
  google: 'google',
  facebook: 'facebook',
}

/** Builds the login app URL, encoding `returnUrl` and only the explicitly-set
 *  `methods` fields as query params (e.g. `?returnUrl=...&google=false`) —
 *  omitted fields are left out entirely rather than sent as `undefined`, so
 *  the login app's default-when-absent behavior stays intact. */
function buildLoginAppUrl(target: string, methods?: LoginMethodFlags, handoffMode?: 'token' | 'code'): string {
  const params = new URLSearchParams({ returnUrl: target })
  if (methods) {
    for (const [field, param] of Object.entries(METHOD_FLAG_PARAMS) as [keyof LoginMethodFlags, string][]) {
      const value = methods[field]
      if (value !== undefined) {
        params.set(param, String(value))
      }
    }
  }
  // Only sent when explicitly 'code' — omitted entirely for the legacy default so the URL
  // shape (and ReturnUrlRedirectHandler's behavior) is byte-identical to before this prop
  // existed for every app that hasn't opted in.
  if (handoffMode === 'code') {
    params.set('handoff', 'code')
  }
  return `${LOGIN_APP_URL}/login?${params.toString()}`
}

/**
 * Builds the login app's `/logout` URL, encoding `returnUrl` the same way
 * `buildLoginAppUrl` does. Exported (unlike `buildLoginAppUrl`) so a
 * consuming app's own logout handler — e.g. `UserMenuButton`'s centralized
 * `handleLogout` — can send the browser here after clearing its own local
 * session, instead of falling through to this page's auto-redirect-to-`/login`
 * behavior, which would silently reuse whatever session the login app's own
 * origin still has (see LogoutPage.tsx in src/passport/apps/login for why
 * that distinction matters: /login reuses an existing session on purpose,
 * /logout unconditionally clears it first).
 *
 * `handoffMode`, when `'code'`, appends `&handoff=code` — same contract as
 * `buildLoginAppUrl`'s own `handoffMode` param. Without this, an app that
 * migrated its sign-IN flow to the exchange-code handoff would still silently
 * fall back to the legacy `?token=<raw JWT>` handoff on its very next
 * sign-IN after a logout, since LogoutPage.tsx's own redirect back to
 * `/login` has no other way to know the app expects `?code=` instead of
 * `?token=` — confirmed via live HAR capture: a real logout+re-login cycle
 * on flow-studio (which passes `handoffMode="code"` here) produced a raw
 * JWT in the URL because this function had no way to carry that preference
 * through `/logout`. Omitted (or `'token'`) for every app that hasn't
 * migrated — byte-identical URL to before this param existed.
 */
export function buildLogoutAppUrl(target: string, handoffMode?: 'token' | 'code'): string {
  const params = new URLSearchParams({ returnUrl: target })
  if (handoffMode === 'code') {
    params.set('handoff', 'code')
  }
  return `${LOGIN_APP_URL}/logout?${params.toString()}`
}

/**
 * `LoginRedirectPage` — NOT a login form. Per the repo owner's explicit
 * constraint, "all login happens through the login app, no compromise" — no
 * BizFirst app should render its own email/password/Google-Sign-In UI. This
 * page's only real job is sending the browser to the real, tested login app
 * (`src/passport/apps/login`) with a `returnUrl`, so it can redirect back
 * with a token once the user actually authenticates there (see
 * `src/passport/apps/login/src/ReturnUrlRedirectHandler.tsx` for that side).
 *
 * Lives here, not per-app, for the same reason `UserMenuButton`/`SignInButton`
 * do — one implementation, reused by every app that needs this page instead
 * of each hand-rolling (and re-styling) its own copy.
 *
 */
export function LoginRedirectPage({ appName, apiBaseUrl, returnUrl, methods, handoffMode }: LoginRedirectPageProps): React.ReactElement {
  // Guards against React.StrictMode's dev-mode double-invocation of the auto-redirect effect
  // below — without it, two window.location.href assignments fire back-to-back on the same
  // mount (harmless here since both target the same URL, but wasteful and a latent hazard if
  // this effect's target ever becomes state-dependent). A ref survives StrictMode's
  // double-invoke on the same component instance, unlike effect-local state.
  const hasRedirected = useRef(false)
  const [loopDetected, setLoopDetected] = useState(false)

  const handleSignIn = () => {
    const target = returnUrl ?? window.location.href
    window.location.href = buildLoginAppUrl(target, methods, handoffMode)
  }

  /** Manual escape hatch from the loop-detected screen: clears every local
   *  trace of the stuck session (not just this store's key, since we don't
   *  know here which app-specific auth store is in play) before trying
   *  again, so a genuinely corrupt local session can't just re-loop
   *  immediately. */
  const handleClearSessionAndRetry = () => {
    if (enableConsoleLogs.loginProcess) {
      console.log('[LoginRedirectPage] User clicked "Clear local session & try again"')
    }
    sessionStorage.removeItem(SSO_HANDOFF_ATTEMPT_STORAGE_KEY)
    localStorage.clear()
    handleSignIn()
  }

  const handleRetry = () => {
    if (enableConsoleLogs.loginProcess) {
      console.log('[LoginRedirectPage] User clicked "Try again"')
    }
    sessionStorage.removeItem(SSO_HANDOFF_ATTEMPT_STORAGE_KEY)
    handleSignIn()
  }

  // Auto-redirect on mount — this page is a pure handoff, not a real choice
  // point (see the class doc comment: "NOT a login form"), so making the
  // user click a button just to get to the actual login form was an
  // unnecessary extra step. The button below still exists as a fallback for
  // anyone who navigates back here after the auto-redirect (e.g. via the
  // browser back button) without another auto-redirect re-firing.
  //
  // useLayoutEffect, not useEffect: useEffect only runs after the browser
  // has painted, so the card below (logo, "Redirecting to sign in…", the
  // buttons) would flash on screen for a moment at this app's own origin
  // before window.location.href actually navigates away — looking exactly
  // like an unwanted intermediate login screen. useLayoutEffect runs
  // synchronously right after DOM mutations but before paint, so the
  // navigation fires before this component's first frame is ever drawn.
  useLayoutEffect(() => {
    if (hasRedirected.current) return
    hasRedirected.current = true

    const attempts = Number(sessionStorage.getItem(SSO_HANDOFF_ATTEMPT_STORAGE_KEY) || '0') + 1
    sessionStorage.setItem(SSO_HANDOFF_ATTEMPT_STORAGE_KEY, String(attempts))

    if (enableConsoleLogs.loginProcess) {
      console.log('[LoginRedirectPage] Mounted — bounced back here without a completed handoff', {
        appName,
        attempts,
        maxAttempts: MAX_AUTO_REDIRECT_ATTEMPTS,
        returnUrl: returnUrl ?? window.location.href,
        handoffMode: handoffMode ?? 'token',
      })
    }

    if (attempts > MAX_AUTO_REDIRECT_ATTEMPTS) {
      // Bounced back to this exact page this many times in a row without a
      // handoff ever completing — auto-redirecting again would just
      // continue the loop silently, with the user never learning why the
      // app never finishes loading. Stop and show them what's happening.
      if (enableConsoleLogs.loginProcess) {
        console.log('[LoginRedirectPage] LOOP DETECTED — attempts exceeded max, showing "keeps redirecting" screen instead of auto-redirecting again', {
          attempts,
          maxAttempts: MAX_AUTO_REDIRECT_ATTEMPTS,
        })
      }
      setLoopDetected(true)
      return
    }

    if (enableConsoleLogs.loginProcess) {
      console.log('[LoginRedirectPage] Auto-redirecting to login app', {
        target: buildLoginAppUrl(returnUrl ?? window.location.href, methods, handoffMode),
      })
    }
    handleSignIn()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (loopDetected) {
    return (
      <div className="auth-login-redirect-page">
        <div className="auth-login-redirect-card">
          <div className="auth-login-redirect-brand">
            <TenantName as="p" className="auth-login-redirect-tenant" />
            <TenantLogo apiBaseUrl={apiBaseUrl} alt="BizFirst AI" width={120} />
            <h1 className="auth-login-redirect-title">{appName}</h1>
            <p className="auth-login-redirect-subtitle">
              Sign-in keeps redirecting back here without completing. This usually means the
              session couldn't be verified. Try again, or clear the local session first if it
              keeps happening.
            </p>
          </div>

          <button type="button" className="auth-login-redirect-submit" onClick={handleRetry}>
            Try again
          </button>
          <button
            type="button"
            className="auth-login-redirect-submit auth-login-redirect-submit-secondary"
            onClick={handleClearSessionAndRetry}
          >
            Clear local session &amp; try again
          </button>
        </div>

        <style>{`
          .auth-login-redirect-page {
            display: flex;
            align-items: center;
            justify-content: center;
            height: 100vh;
            width: 100%;
            background: var(--color-bg, var(--bg-primary, #0d1117));
          }

          .auth-login-redirect-card {
            width: 100%;
            max-width: 380px;
            padding: 32px;
            background: var(--color-bg-secondary, var(--bg-secondary, #1c1f26));
            border: 1px solid var(--color-border, var(--border, #30363d));
            border-radius: 8px;
            box-shadow: 0 10px 25px rgba(0, 0, 0, 0.25);
          }

          .auth-login-redirect-brand {
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
            margin-bottom: 24px;
          }

          .auth-login-redirect-tenant {
            margin: 0 0 12px;
            font-size: 12px;
            font-weight: 600;
            letter-spacing: 0.04em;
            text-transform: uppercase;
            color: var(--color-text-secondary, var(--text-secondary, #8b949e));
          }

          .auth-login-redirect-title {
            margin: 16px 0 4px;
            font-size: 20px;
            font-weight: 600;
            color: var(--color-text-primary, var(--text-primary, #e6edf3));
          }

          .auth-login-redirect-subtitle {
            margin: 0;
            font-size: 14px;
            color: var(--color-text-secondary, var(--text-secondary, #8b949e));
          }

          .auth-login-redirect-submit {
            width: 100%;
            height: 40px;
            background: var(--color-primary, var(--obs-primary, #309B48));
            border: none;
            border-radius: 6px;
            color: white;
            font-size: 14px;
            font-weight: 600;
            cursor: pointer;
            transition: opacity 0.2s ease;
            margin-top: 12px;
          }

          .auth-login-redirect-submit:hover {
            opacity: 0.9;
          }

          .auth-login-redirect-submit-secondary {
            background: transparent;
            border: 1px solid var(--color-border, var(--border, #30363d));
            color: var(--color-text-primary, var(--text-primary, #e6edf3));
          }
        `}</style>
      </div>
    )
  }

  return (
    <div className="auth-login-redirect-page">
      <div className="auth-login-redirect-card">
        <div className="auth-login-redirect-brand">
          <TenantName as="p" className="auth-login-redirect-tenant" />
          <TenantLogo apiBaseUrl={apiBaseUrl} alt="BizFirst AI" width={120} />
          <h1 className="auth-login-redirect-title">{appName}</h1>
          <p className="auth-login-redirect-subtitle">Redirecting to sign in…</p>
        </div>

        <button type="button" className="auth-login-redirect-submit" onClick={handleSignIn}>
          Sign in
        </button>
      </div>

      <style>{`
        .auth-login-redirect-page {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100vh;
          width: 100%;
          background: var(--color-bg, var(--bg-primary, #0d1117));
        }

        .auth-login-redirect-card {
          width: 100%;
          max-width: 380px;
          padding: 32px;
          background: var(--color-bg-secondary, var(--bg-secondary, #1c1f26));
          border: 1px solid var(--color-border, var(--border, #30363d));
          border-radius: 8px;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.25);
        }

        .auth-login-redirect-brand {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          margin-bottom: 24px;
        }

        .auth-login-redirect-tenant {
          margin: 0 0 12px;
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: var(--color-text-secondary, var(--text-secondary, #8b949e));
        }

        .auth-login-redirect-title {
          margin: 16px 0 4px;
          font-size: 20px;
          font-weight: 600;
          color: var(--color-text-primary, var(--text-primary, #e6edf3));
        }

        .auth-login-redirect-subtitle {
          margin: 0;
          font-size: 14px;
          color: var(--color-text-secondary, var(--text-secondary, #8b949e));
        }

        .auth-login-redirect-submit {
          width: 100%;
          height: 40px;
          background: var(--color-primary, var(--obs-primary, #309B48));
          border: none;
          border-radius: 6px;
          color: white;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: opacity 0.2s ease;
        }

        .auth-login-redirect-submit:hover {
          opacity: 0.9;
        }
      `}</style>
    </div>
  )
}

export default LoginRedirectPage
